package com.sweetlime.dungeonamigo.presentation.features.dice

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.sweetlime.dungeonamigo.model.DiceRoll
import com.sweetlime.dungeonamigo.model.DiceType
import com.sweetlime.dungeonamigo.model.SingleRoll
import com.sweetlime.dungeonamigo.util.currentTimeMillis
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlin.random.Random

enum class AdvantageState { NORMAL, ADVANTAGE, DISADVANTAGE }

class DiceViewModel : ViewModel() {

    var onRollCompleted: ((DiceRoll, String) -> Unit)? = null

    private val _selectedDice = MutableStateFlow<Map<DiceType, Int>>(emptyMap())
    val selectedDice: StateFlow<Map<DiceType, Int>> = _selectedDice.asStateFlow()

    private val _modifier = MutableStateFlow(0)
    val modifier: StateFlow<Int> = _modifier.asStateFlow()

    private val _advantage = MutableStateFlow(AdvantageState.NORMAL)
    val advantage: StateFlow<AdvantageState> = _advantage.asStateFlow()

    private val _lastRoll = MutableStateFlow<DiceRoll?>(null)
    val lastRoll: StateFlow<DiceRoll?> = _lastRoll.asStateFlow()

    private val _rollHistory = MutableStateFlow<List<DiceRoll>>(emptyList())
    val rollHistory: StateFlow<List<DiceRoll>> = _rollHistory.asStateFlow()

    private val _isAnimating = MutableStateFlow(false)
    val isAnimating: StateFlow<Boolean> = _isAnimating.asStateFlow()

    private val _displayDice = MutableStateFlow<List<SingleRoll>>(emptyList())
    val displayDice: StateFlow<List<SingleRoll>> = _displayDice.asStateFlow()

    fun addDie(type: DiceType) {
        val updated = _selectedDice.value.toMutableMap()
        updated[type] = (updated[type] ?: 0) + 1
        _selectedDice.value = updated
    }

    fun removeDie(type: DiceType) {
        val updated = _selectedDice.value.toMutableMap()
        val count = updated[type] ?: return
        if (count <= 1) updated.remove(type) else updated[type] = count - 1
        _selectedDice.value = updated
    }

    fun clearDice() {
        _selectedDice.value = emptyMap()
        _modifier.value = 0
        _advantage.value = AdvantageState.NORMAL
        _lastRoll.value = null
        _displayDice.value = emptyList()
    }

    fun setModifier(value: Int) {
        _modifier.value = value.coerceIn(-10, 10)
    }

    fun setAdvantage(state: AdvantageState) {
        _advantage.value = state
    }

    fun roll(label: String = "") {
        val dice = _selectedDice.value
        if (dice.isEmpty()) return
        if (_isAnimating.value) return

        val modifier = _modifier.value
        val advantage = _advantage.value
        val singleD20 = dice[DiceType.D20] == 1 && (advantage != AdvantageState.NORMAL)

        val rolls = mutableListOf<SingleRoll>()
        if (singleD20) {
            val r1 = Random.nextInt(1, 21)
            val r2 = Random.nextInt(1, 21)
            val (keep, drop) = if (advantage == AdvantageState.ADVANTAGE)
                maxOf(r1, r2) to minOf(r1, r2)
            else
                minOf(r1, r2) to maxOf(r1, r2)
            rolls += SingleRoll(DiceType.D20, keep, false)
            rolls += SingleRoll(DiceType.D20, drop, true)
            dice.entries.filter { it.key != DiceType.D20 }.forEach { (type, count) ->
                repeat(count) { rolls += SingleRoll(type, Random.nextInt(1, type.sides + 1)) }
            }
        } else {
            dice.entries.forEach { (type, count) ->
                repeat(count) { rolls += SingleRoll(type, Random.nextInt(1, type.sides + 1)) }
            }
        }

        val result = DiceRoll(
            dice = rolls,
            modifier = modifier,
            label = label,
            isAdvantage = singleD20 && advantage == AdvantageState.ADVANTAGE,
            isDisadvantage = singleD20 && advantage == AdvantageState.DISADVANTAGE,
            timestamp = currentTimeMillis(),
        )

        _isAnimating.value = true
        _displayDice.value = randomDisplayDice(dice, singleD20)

        viewModelScope.launch {
            // Phase 1: fast shuffle at 60ms for 600ms
            repeat(10) {
                delay(60)
                _displayDice.value = randomDisplayDice(dice, singleD20)
            }
            // Phase 2: slow down
            val slowIntervals = listOf(100L, 150L, 200L, 300L)
            slowIntervals.forEach { interval ->
                delay(interval)
                _displayDice.value = randomDisplayDice(dice, singleD20)
            }
            // Phase 3: snap to result
            _displayDice.value = result.dice
            delay(200)

            _lastRoll.value = result
            _rollHistory.value = (listOf(result) + _rollHistory.value).take(10)
            onRollCompleted?.invoke(result, label)
            _isAnimating.value = false
        }
    }

    private fun randomDisplayDice(dice: Map<DiceType, Int>, singleD20: Boolean): List<SingleRoll> {
        val display = mutableListOf<SingleRoll>()
        if (singleD20) {
            repeat(2) { display += SingleRoll(DiceType.D20, Random.nextInt(1, 21), it == 1) }
            dice.entries.filter { it.key != DiceType.D20 }.forEach { (type, count) ->
                repeat(count) { display += SingleRoll(type, Random.nextInt(1, type.sides + 1)) }
            }
        } else {
            dice.entries.forEach { (type, count) ->
                repeat(count) { display += SingleRoll(type, Random.nextInt(1, type.sides + 1)) }
            }
        }
        return display
    }

    fun rerollFromHistory(roll: DiceRoll) {
        val diceMap = mutableMapOf<DiceType, Int>()
        if (roll.isAdvantage || roll.isDisadvantage) {
            diceMap[DiceType.D20] = 1
            roll.dice.filter { it.diceType != DiceType.D20 }.forEach {
                diceMap[it.diceType] = (diceMap[it.diceType] ?: 0) + 1
            }
        } else {
            roll.dice.forEach { diceMap[it.diceType] = (diceMap[it.diceType] ?: 0) + 1 }
        }
        _selectedDice.value = diceMap
        _modifier.value = roll.modifier
        _advantage.value = when {
            roll.isAdvantage -> AdvantageState.ADVANTAGE
            roll.isDisadvantage -> AdvantageState.DISADVANTAGE
            else -> AdvantageState.NORMAL
        }
        this.roll(roll.label)
    }

    fun preConfigureForAbility(abilityName: String, abilityModifier: Int) {
        _selectedDice.value = mapOf(DiceType.D20 to 1)
        _modifier.value = abilityModifier
        _advantage.value = AdvantageState.NORMAL
        _lastRoll.value = null
        _displayDice.value = emptyList()
    }

    fun preConfigureForSkill(skillName: String, bonus: Int) {
        _selectedDice.value = mapOf(DiceType.D20 to 1)
        _modifier.value = bonus
        _advantage.value = AdvantageState.NORMAL
        _lastRoll.value = null
        _displayDice.value = emptyList()
    }
}
